import { useEffect } from 'react';
import useIdentity from '../useIdentity';
import { useProductSettings, type ProductSettings } from '@workspace/utils-react';
import { AuthProvider } from '../../../cash-forecaster/src/providers/AuthProvider';
import { LoadingScreen } from '../../../cash-forecaster/src/components/LoadingScreen';
import { ApiClientProvider } from '../../../cash-forecaster/src/providers/ApiClientProvider';
import type { ReactNode } from 'react';

interface AuthBridgeProviderProps {
  instanceId?: string;
  children: ReactNode;
}

export function AuthBridgeProvider({ instanceId, children }: AuthBridgeProviderProps): React.ReactElement {
  const { getAccessToken, signin, loading, error, isAuthenticated, user } = useIdentity();
  const settings = useProductSettings<ProductSettings>();

  useEffect(() => {
    if (!isAuthenticated && !loading && !error) {
      signin();
    }
  }, [isAuthenticated, loading, error, signin]);

  useEffect(() => {
    if (error && (error as any)?.message === 'login_required' && settings?.workspaceUrl) {
      const message = {
        type: 'RequestReauthentication',
      };
      window.parent.postMessage(JSON.stringify(message), settings.workspaceUrl);
    }
  }, [error, settings?.workspaceUrl]);

  if (loading || (!isAuthenticated && !error)) {
    return <LoadingScreen />;
  }

  if (error) {
    return (
      <div className="error-container">
        <h2>Authentication Error: {error?.message}</h2>
      </div>
    );
  }

  if (isAuthenticated && user) {
    // Extract user info for Evo MFE integration
    const evoUserInfo = {
      userId: user.profile.sub || '',
      email: user.profile.email || '',
      firstName: user.profile.given_name || '',
      lastName: user.profile.family_name || '',
      customerName: instanceId || '',
    };

    return (
      <AuthProvider getAccessToken={getAccessToken} instanceId={instanceId} evoUserInfo={evoUserInfo}>
        <ApiClientProvider apiUrl={settings?.apiUrl} apiGraphqlUrl={settings?.apiGraphqlUrl}>
          {children}
        </ApiClientProvider>
      </AuthProvider>
    );
  }

  return <LoadingScreen />;
}
